import productTrackingMapping from 'root/utils/tracking/GA4/productTrackingMapping.json';
import type {
    GTMEvent,
    incompleteGTMEvent,
} from 'root/utils/tracking/GA4/googleTagManager';
import { getDecodedValueFromLocalStorage } from 'root/utils/dom/localStorage';
import { PlacementEnums } from 'root/utils/tracking/GA4/types';

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type FormEventAction =
    | 'form_submit'
    | 'form_success'
    | 'form_error'
    | 'form_in_page'
    | 'form_lightbox_open'
    | 'form_lightbox_closed'
    | 'form_email_opt_in'
    | 'form_email_opt_out'
    | 'field_completed'
    | 'field_displayed';
class FormGTMObjectConstructor {
    #form: HTMLFormElement;
    #fields: Field[];
    #incompleteFormGTMObject: incompleteGTMEvent;
    #isGolfProduct: boolean;

    constructor(form: HTMLFormElement) {
        this.#form = form;
        this.#fields = Array.from(form.elements).filter(element => {
            return 'type' in element ? element.type !== 'button' : false;
        }) as Field[];
        this.#isGolfProduct = this.#getFieldValue('product')?.toLowerCase() === 'golf';
        this.#incompleteFormGTMObject = {};
    }
    #getFieldValue(fieldName: string, isHidden = true) {
        if (isHidden) {
            return this.#fields.find(field => field.name === fieldName)?.value || null;
        }
        return (
            this.#fields.find(
                field => field.type !== 'hidden' && field.name === fieldName
            )?.value || null
        );
    }

    #getTrackingValue(
        trackingKey: string,
        eventAction: FormEventAction,
        completedFieldValue?: string,
        fieldName?: string
    ) {
        switch (trackingKey) {
            case 'form_name': {
                const fieldValue = this.#getFieldValue('form_name');
                return this.#form.dataset.formTrackingName ?? fieldValue;
            }
            case 'product_type': {
                /* This is used for non-wizard agnostic forms where the product can be chosen from the select field */
                const productValueFromSelectField =
                    this.#getFieldValue('product', false)?.toLowerCase() ?? null;

                /* This is used for non-wizard product-related forms (Retail, or Resto, or Golf) */
                const productValueFromHiddenInputField =
                    this.#getFieldValue('product')?.toLowerCase() ?? null;

                const productFieldValue =
                    productValueFromSelectField || productValueFromHiddenInputField;

                const mappingObject: Record<string, string> = productTrackingMapping;

                let productValue;
                if (productFieldValue) {
                    productValue = mappingObject[productFieldValue] ?? productFieldValue;
                }

                const blogProductFieldValue = this.#getFieldValue('blog_product')
                    ?.split('.')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(', ');

                return productValue ?? blogProductFieldValue;
            }
            case 'stage':
                return this.#getFieldValue('stage');
            case 'role':
                if (!['form_success', 'form_submit'].includes(eventAction)) {
                    return null;
                }

                return this.#isGolfProduct && this.#getFieldValue('role');
            case 'business_type':
                if (
                    this.#isGolfProduct ||
                    !['form_success', 'form_submit'].includes(eventAction)
                )
                    return null;
                return (
                    this.#getFieldValue('resto_business_type') ??
                    this.#getFieldValue('agnostic_business_type') ??
                    this.#getFieldValue('business_type') ?? // business_type is for retail
                    this.#getFieldValue('business_type_slug')
                        ?.split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                );
            case 'gtv_value':
                if (!['form_success', 'form_submit'].includes(eventAction)) {
                    return null;
                }
                return this.#getFieldValue('estimated_annual_gtv');
            case 'field_value':
                return completedFieldValue;

            case 'field_name':
                return fieldName;
            case 'internal_test': {
                const emailValue = this.#getFieldValue('email') ?? '';
                if (emailValue && eventAction === 'form_success') {
                    return emailValue
                        ?.split('@')[1]
                        ?.match(/lightspeedhq\.[a-z]{0,4}(\.[a-z]{0,4})?/)
                        ? '1'
                        : '0';
                }
                break;
            }
            case 'placement':
                return getFormPlacementFromDataAttribute(this.#form);
        }
    }
    getFormGTMObject(
        eventAction: FormEventAction,
        completedFieldValue?: string,
        fieldName?: string
    ): GTMEvent {
        const trackingKeys = [
            'form_name',
            'product_type',
            'stage',
            'role',
            'business_type',
            'gtv_value',
            'field_value',
            'field_name',
            'internal_test',
            'placement',
        ];

        // Here we loop through each trackingKey (E.g. 'business_type') that we want to populate and send to Google Analytics
        for (const trackingKey of trackingKeys) {
            const trackingValue = this.#getTrackingValue(
                trackingKey,
                eventAction,
                completedFieldValue,
                fieldName
            );
            if (trackingValue) {
                this.#incompleteFormGTMObject[trackingKey] = trackingValue;
            }
        }

        return Object.assign(
            { event_action: eventAction },
            this.#incompleteFormGTMObject
        );
    }
}

const getPlacement = (element: HTMLElement) => {
    if (element.closest('.footer-main')) {
        return PlacementEnums.footer;
    }
    if (element.closest('.main-site-header-btf')) {
        return PlacementEnums.nav;
    }
    if (element.closest('[data-hero-section]')) {
        return PlacementEnums.hero;
    }
    return PlacementEnums.body;
};

const getFormPlacementFromDataAttribute = (form: HTMLFormElement) => {
    return form.dataset.formPlacement ?? getPlacement(form);
};

export { FormGTMObjectConstructor, getPlacement, getFormPlacementFromDataAttribute };
